/**
 * Recommendation Card Component
 *
 * Individual optimization recommendation display with actions
 *
 * Features:
 * - Recommendation details display
 * - Priority and confidence indicators
 * - Impact metrics preview
 * - Action buttons (accept/reject/implement)
 * - Evidence attribution
 */

import React from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Badge,
  Button,
  Progress,
  Tooltip,
  ThemeIcon,
  Divider,
  Accordion,
  List,
  Paper,
  Grid
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconRocket,
  IconSparkles,
  IconAlertTriangle,
  IconClock,
  IconTrendingUp,
  IconBolt,
  IconCoin,
  IconShield,
  IconChartBar,
  IconInfoCircle
} from '@tabler/icons-react';
import type {
  OptimizationRecommendation,
  RecommendationPriority,
  RecommendationType,
  RecommendationStatus,
  ImplementationComplexity
} from '../hooks/useOptimizationRecommendations';

interface RecommendationCardProps {
  recommendation: OptimizationRecommendation;
  onAccept?: () => void;
  onReject?: () => void;
  onImplement?: () => void;
  onViewDetails?: () => void;
  compact?: boolean;
}

export function RecommendationCard({
  recommendation,
  onAccept,
  onReject,
  onImplement,
  onViewDetails,
  compact = false
}: RecommendationCardProps) {
  /**
   * Get priority color
   */
  const getPriorityColor = (priority: RecommendationPriority): string => {
    switch (priority) {
      case 'emergency': return 'red';
      case 'critical': return 'orange';
      case 'high': return 'yellow';
      case 'medium': return 'blue';
      case 'low': return 'gray';
      default: return 'gray';
    }
  };

  /**
   * Get type icon and color
   */
  const getTypeDisplay = (type: RecommendationType): { icon: React.ReactNode; color: string; label: string } => {
    switch (type) {
      case 'performance':
        return { icon: <IconBolt size={16} />, color: 'yellow', label: 'Performance' };
      case 'cost':
        return { icon: <IconCoin size={16} />, color: 'green', label: 'Cost' };
      case 'safety':
        return { icon: <IconShield size={16} />, color: 'red', label: 'Safety' };
      case 'compliance':
        return { icon: <IconCheck size={16} />, color: 'blue', label: 'Compliance' };
      case 'usability':
        return { icon: <IconSparkles size={16} />, color: 'violet', label: 'Usability' };
      case 'reliability':
        return { icon: <IconShield size={16} />, color: 'teal', label: 'Reliability' };
      case 'scalability':
        return { icon: <IconTrendingUp size={16} />, color: 'cyan', label: 'Scalability' };
      case 'maintainability':
        return { icon: <IconChartBar size={16} />, color: 'indigo', label: 'Maintainability' };
      default:
        return { icon: <IconSparkles size={16} />, color: 'gray', label: type };
    }
  };

  /**
   * Get status color
   */
  const getStatusColor = (status: RecommendationStatus): string => {
    switch (status) {
      case 'pending': return 'blue';
      case 'under_review': return 'cyan';
      case 'accepted': return 'green';
      case 'rejected': return 'red';
      case 'in_progress': return 'yellow';
      case 'implemented': return 'teal';
      case 'failed': return 'red';
      case 'reverted': return 'orange';
      default: return 'gray';
    }
  };

  /**
   * Get complexity color
   */
  const getComplexityColor = (complexity: ImplementationComplexity): string => {
    switch (complexity) {
      case 'trivial': return 'green';
      case 'low': return 'teal';
      case 'medium': return 'blue';
      case 'high': return 'yellow';
      case 'very_high': return 'orange';
      case 'complex': return 'red';
      default: return 'gray';
    }
  };

  /**
   * Get confidence color
   */
  const getConfidenceColor = (score: number): string => {
    if (score >= 0.9) return 'green';
    if (score >= 0.8) return 'teal';
    if (score >= 0.7) return 'blue';
    if (score >= 0.6) return 'yellow';
    return 'orange';
  };

  /**
   * Format date
   */
  const formatDate = (isoString: string): string => {
    return new Date(isoString).toLocaleDateString();
  };

  /**
   * Format time savings
   */
  const formatTimeSavings = (seconds?: number): string => {
    if (!seconds) return 'N/A';
    const hours = Math.round(seconds / 3600);
    if (hours < 1) return `${Math.round(seconds / 60)}min`;
    if (hours < 24) return `${hours}h`;
    return `${Math.round(hours / 24)}d`;
  };

  const typeDisplay = getTypeDisplay(recommendation.recommendationType);
  const canAccept = recommendation.status === 'pending' || recommendation.status === 'under_review';
  const canReject = recommendation.status === 'pending' || recommendation.status === 'under_review';
  const canImplement = recommendation.status === 'accepted' || recommendation.status === 'in_progress';

  return (
    <Card withBorder padding={compact ? 'sm' : 'md'}>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" style={{ flex: 1 }}>
            <ThemeIcon color={typeDisplay.color} variant="light">
              {typeDisplay.icon}
            </ThemeIcon>
            <div style={{ flex: 1 }}>
              <Group gap="xs">
                <Text fw={600} size={compact ? 'sm' : 'md'}>{recommendation.name}</Text>
                <Badge size="xs" color={getPriorityColor(recommendation.priority)}>
                  {recommendation.priority}
                </Badge>
                <Badge size="xs" color={getStatusColor(recommendation.status)}>
                  {recommendation.status.replace('_', ' ')}
                </Badge>
              </Group>
              {!compact && (
                <Text size="sm" c="dimmed" mt="xs" lineClamp={2}>
                  {recommendation.description}
                </Text>
              )}
            </div>
          </Group>

          {/* Confidence Score */}
          <Tooltip label={`AI Confidence: ${Math.round(recommendation.confidenceScore * 100)}%`}>
            <div style={{ textAlign: 'center' }}>
              <Progress
                value={recommendation.confidenceScore * 100}
                color={getConfidenceColor(recommendation.confidenceScore)}
                size="sm"
                style={{ width: 60 }}
              />
              <Text size="xs" c="dimmed" mt={2}>
                {Math.round(recommendation.confidenceScore * 100)}%
              </Text>
            </div>
          </Tooltip>
        </Group>

        {/* Impact Metrics */}
        {!compact && (
          <Grid>
            {recommendation.impactAnalysis.timeSavingsSeconds && (
              <Grid.Col span={3}>
                <Paper p="xs" withBorder bg="blue.0">
                  <Group gap="xs">
                    <IconClock size={14} color="var(--mantine-color-blue-6)" />
                    <div>
                      <Text size="xs" c="dimmed">Time Saved</Text>
                      <Text size="sm" fw={600}>
                        {formatTimeSavings(recommendation.impactAnalysis.timeSavingsSeconds)}
                      </Text>
                    </div>
                  </Group>
                </Paper>
              </Grid.Col>
            )}
            {recommendation.impactAnalysis.costSavingsPercentage && (
              <Grid.Col span={3}>
                <Paper p="xs" withBorder bg="green.0">
                  <Group gap="xs">
                    <IconCoin size={14} color="var(--mantine-color-green-6)" />
                    <div>
                      <Text size="xs" c="dimmed">Cost Saved</Text>
                      <Text size="sm" fw={600}>
                        {Math.round(recommendation.impactAnalysis.costSavingsPercentage)}%
                      </Text>
                    </div>
                  </Group>
                </Paper>
              </Grid.Col>
            )}
            {recommendation.impactAnalysis.errorReductionPercentage && (
              <Grid.Col span={3}>
                <Paper p="xs" withBorder bg="red.0">
                  <Group gap="xs">
                    <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
                    <div>
                      <Text size="xs" c="dimmed">Error Reduction</Text>
                      <Text size="sm" fw={600}>
                        {Math.round(recommendation.impactAnalysis.errorReductionPercentage)}%
                      </Text>
                    </div>
                  </Group>
                </Paper>
              </Grid.Col>
            )}
            {recommendation.impactAnalysis.estimatedRoi && (
              <Grid.Col span={3}>
                <Paper p="xs" withBorder bg="violet.0">
                  <Group gap="xs">
                    <IconTrendingUp size={14} color="var(--mantine-color-violet-6)" />
                    <div>
                      <Text size="xs" c="dimmed">Est. ROI</Text>
                      <Text size="sm" fw={600}>
                        ${Math.round(recommendation.impactAnalysis.estimatedRoi).toLocaleString()}
                      </Text>
                    </div>
                  </Group>
                </Paper>
              </Grid.Col>
            )}
          </Grid>
        )}

        {/* Metadata */}
        <Group gap="md">
          <Tooltip label="Implementation Complexity">
            <Badge size="sm" color={getComplexityColor(recommendation.complexity)} variant="light">
              {recommendation.complexity}
            </Badge>
          </Tooltip>
          <Tooltip label="Generated by AI Model">
            <Badge size="sm" variant="light" leftSection={<IconSparkles size={12} />}>
              {recommendation.modelInfo.modelName}
            </Badge>
          </Tooltip>
          <Text size="xs" c="dimmed">Created {formatDate(recommendation.createdAt)}</Text>
          {recommendation.requiresApproval && (
            <Tooltip label="Requires manual approval before implementation">
              <Badge size="xs" color="orange" variant="light">
                Approval Required
              </Badge>
            </Tooltip>
          )}
        </Group>

        {/* Details Accordion */}
        {!compact && (
          <Accordion variant="contained">
            <Accordion.Item value="details">
              <Accordion.Control icon={<IconInfoCircle size={16} />}>
                View Details
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  {/* Target Information */}
                  {recommendation.targetWorkflowId && (
                    <div>
                      <Text size="sm" fw={600}>Target Workflow</Text>
                      <Text size="sm" c="dimmed">{recommendation.targetWorkflowId}</Text>
                    </div>
                  )}

                  {/* Compliance Frameworks */}
                  {recommendation.complianceFrameworks.length > 0 && (
                    <div>
                      <Text size="sm" fw={600} mb="xs">Compliance Frameworks</Text>
                      <Group gap="xs">
                        {recommendation.complianceFrameworks.map(fw => (
                          <Badge key={fw} size="sm" variant="outline">{fw}</Badge>
                        ))}
                      </Group>
                    </div>
                  )}

                  {/* Evidence */}
                  {recommendation.evidence && (
                    <div>
                      <Text size="sm" fw={600} mb="xs">Evidence</Text>
                      <Paper p="xs" withBorder bg="gray.0">
                        <Text size="xs" style={{ fontFamily: 'monospace' }}>
                          {JSON.stringify(recommendation.evidence, null, 2).slice(0, 200)}...
                        </Text>
                      </Paper>
                    </div>
                  )}

                  {/* Implementation Progress */}
                  {recommendation.implementationProgress !== undefined && (
                    <div>
                      <Text size="sm" fw={600} mb="xs">Implementation Progress</Text>
                      <Progress value={recommendation.implementationProgress} size="lg" />
                      <Text size="xs" c="dimmed" mt="xs">
                        {recommendation.implementationProgress}% complete
                      </Text>
                    </div>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}

        {/* Actions */}
        <Divider />
        <Group justify="flex-end">
          {canAccept && onAccept && (
            <Button
              size="sm"
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={onAccept}
            >
              Accept
            </Button>
          )}
          {canReject && onReject && (
            <Button
              size="sm"
              color="red"
              variant="light"
              leftSection={<IconX size={16} />}
              onClick={onReject}
            >
              Reject
            </Button>
          )}
          {canImplement && onImplement && (
            <Button
              size="sm"
              color="blue"
              leftSection={<IconRocket size={16} />}
              onClick={onImplement}
            >
              Mark Implemented
            </Button>
          )}
          {onViewDetails && (
            <Button
              size="sm"
              variant="default"
              leftSection={<IconInfoCircle size={16} />}
              onClick={onViewDetails}
            >
              Details
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
